/**
 * Dashboard TV ruang kerja — K137.
 *
 * ---------------------------------------------------------------------------
 * ⛔ LAYAR INI DIBACA DARI JARAK BEBERAPA METER, DAN NOL ADA YANG MEMEGANGNYA
 * ---------------------------------------------------------------------------
 *
 *  1. **Huruf besar, kontras tinggi.** Ukuran yang nyaman di meja nol terbaca
 *     dari seberang ruangan. Seluruh angka pentingnya `text-5xl` ke atas.
 *
 *  2. **Nol satu pun kendali.** Nol filter, nol dropdown, nol tombol — setiap
 *     kendali adalah keadaan yang bisa salah dan nol ada yang membetulkannya.
 *
 *  3. **Menyegarkan sendiri, dan MEMBUKTIKANNYA.** Jam "diperbarui" ditampilkan
 *     terus. Angka yang membeku di layar TV LEBIH BURUK daripada nol ada layar,
 *     karena ia terlihat benar — dan jam yang berhenti bergerak langsung
 *     terlihat siapa pun yang lewat.
 *
 *  4. **Yang BELUM selesai sama pentingnya.** Permintaan Owner berbunyi *"tiap
 *     bagian mana yg sudah menyelesaikan tugasnya"*, dan menjawabnya harfiah
 *     menghasilkan papan yang hanya menampilkan yang hijau. Yang perlu dilihat
 *     orang di lantai produksi jam tiga sore adalah bagian yang BELUM.
 *
 * ⚠ Tetap di balik login. TV di ruang kerja cukup login sekali dan dibiarkan;
 *   membuka layar berisi data produksi tanpa login berarti siapa pun yang tahu
 *   alamatnya bisa membacanya dari luar.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { getRegions } from "@/lib/papanKerjaApi";

const SEGARKAN_DETIK = 60;

const KEADAAN = {
  TUNTAS: {
    label: "TUNTAS",
    kelas: "border-success-soft-border bg-success-soft text-success-soft-foreground",
  },
  BERJALAN: {
    label: "BERJALAN",
    kelas: "border-info-soft-border bg-info-soft text-info-soft-foreground",
  },
  BELUM_DISENTUH: {
    label: "BELUM DISENTUH",
    kelas: "border-danger-soft-border bg-danger-soft text-danger-soft-foreground",
  },
  NOL_TUGAS: {
    // ⚠ Sengaja REDUP, bukan merah. Bagian yang memang nol punya tugas hari ini
    //   bukan bagian yang tertinggal — menyamakan keduanya membuat papan
    //   berteriak pada hari yang tenang, lalu diabaikan pada hari yang tidak.
    label: "tidak ada tugas",
    kelas: "border-border bg-muted text-muted-foreground",
  },
};

export default function PapanTV() {
  const { profile } = useAuth();
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");
  const [jam, setJam] = useState("");

  // ⛔ CACAT UAT 31 Agu 2026, KEMBARAN PERSIS papan-kerja 30 Agu: layar ini
  //    mati total untuk akun tanpa region (Owner) — ia menulis "Sebutkan
  //    wilayah yang ingin dilihat" dan nol menyediakan satu pun kendali.
  //
  //    Wilayahnya dibawa lewat URL (?region=...), BUKAN state, dan itu bukan
  //    selera: TV di ruang kerja login sekali lalu ditinggal tanpa operator.
  //    URL yang memuat wilayah bertahan melewati reload/restart; pilihan di
  //    state hilang bersama sesi tab dan papannya kembali mati tanpa ada
  //    yang menyadari.
  const [cari, setCari] = useSearchParams();
  const regionUrl = cari.get("region") || "";
  const regionId = profile?.region_id || regionUrl || undefined;
  const perluPilih = !regionId;
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    if (!perluPilih) return;
    getRegions()
      .then(({ data: d }) => {
        const daftar = d || [];
        setRegions(daftar);
        // Satu wilayah = nol ada yang perlu dipilih.
        if (daftar.length === 1)
          setCari({ region: daftar[0].id }, { replace: true });
      })
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [perluPilih, setCari]);

  const muat = useCallback(async () => {
    if (!regionId) return;
    try {
      const { data: d } = await api.get("/papan-kerja/tv", {
        params: { region_id: regionId },
      });
      setData(d);
      setGalat("");
      setJam(new Date().toLocaleTimeString("id-ID"));
    } catch (e) {
      // ⛔ Galat DITAMPILKAN, bukan ditelan. Layar TV yang diam saat backend
      //    mati terlihat persis sama dengan hari yang sepi.
      setGalat(apiErrorMessage(e));
    }
  }, [regionId]);

  useEffect(() => {
    muat();
    const t = setInterval(muat, SEGARKAN_DETIK * 1000);
    return () => clearInterval(t);
  }, [muat]);

  if (perluPilih) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border-2 border-border bg-card p-10 text-center">
          <p className="text-3xl font-bold text-foreground">Papan ini milik SATU wilayah</p>
          <p className="mt-2 text-xl text-muted-foreground">
            Pilih sekali — pilihannya tersimpan di alamat halaman, jadi TV yang
            dinyalakan ulang kembali ke papan yang sama.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {regions.map((r) => (
              <button key={r.id} type="button"
                      onClick={() => setCari({ region: r.id })}
                      className="rounded-2xl border-2 border-info-soft-border bg-info-soft px-8 py-4 text-2xl font-semibold text-info-soft-foreground transition hover:bg-info/20">
                {r.name}
              </button>
            ))}
          </div>
          {regions.length === 0 && !galat && (
            <p className="mt-4 text-xl text-muted-foreground">Memuat daftar wilayah…</p>
          )}
          {galat && (
            <p className="mt-4 text-xl text-destructive">{galat}</p>
          )}
        </div>
      </div>
    );
  }

  if (galat) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border-2 border-destructive/50 bg-destructive/10 p-10 text-center">
          <p className="text-3xl font-bold text-destructive">Papan tidak bisa dimuat</p>
          <p className="mt-3 text-xl text-destructive/90">{galat}</p>
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-2xl text-muted-foreground">Memuat…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="text-4xl font-bold text-foreground">
          {data.region?.name}
          <span className="ml-4 text-2xl font-normal text-muted-foreground">
            {data.tanggal}
          </span>
        </h1>
        {/* ⛔ Butir 3 — buktinya bahwa layar ini masih hidup. */}
        <span className="text-xl text-muted-foreground">diperbarui {jam}</span>
      </div>

      <div className="rounded-2xl border-2 border-success-soft-border bg-success-soft p-6">
        <p className="text-2xl text-success-soft-foreground">
          Selesai hari ini
        </p>
        <p className="mt-1 text-6xl font-bold tabular-nums text-success-soft-foreground">
          {data.total_atasan_selesai}
          <span className="ml-3 text-3xl font-normal">atasan</span>
        </p>
        {data.selesai_hari_ini.length > 0 ? (
          <p className="mt-2 text-2xl text-success-soft-foreground">
            {data.selesai_hari_ini.map((s) => `${s.wo_number} (${s.jam})`).join(" · ")}
          </p>
        ) : (
          <p className="mt-2 text-2xl text-success-soft-foreground/70">
            Belum ada pesanan yang tuntas hari ini.
          </p>
        )}
      </div>

      {/*
        PESANAN TUNTAS HARI INI — permintaan Owner 2 September 2026:
        *"menampilkan FA yg selesai (berisi nomor FA dan nama team, nama
        pemesan), data diambil dari pesanan yg sudah selesai packing dan
        sudah diserah terima kan ke gudang barang jadi"*.

        ⛔ BEDANYA DENGAN PANEL DI ATAS, dan bedanya penting: panel di atas
           membaca tahap yang DITANDAI selesai. Panel ini membaca SERAH
           TERIMA sungguhan ke gudang. Barang bisa ditandai selesai tanpa
           berpindah — dan papan yang menyebut "tuntas" untuk barang yang
           masih di meja packing akan dibantah orang gudang sekali, lalu
           papan itu berhenti dipercaya seluruhnya.

        ⛔ DUA ARTI DARI KOSONG DIBEDAKAN. `tuntas_jalur_siap` membedakan
           "hari ini belum ada yang tuntas" dari "jalur serah terima ke
           gudang belum ada sama sekali". Yang kedua cacat sistem yang
           menyamar jadi hari yang sepi — persis kelas kegagalan yang papan
           ini ada untuk membongkar.
      */}
      <div className="rounded-2xl border-2 border-info-soft-border bg-info-soft p-6">
        <p className="text-2xl font-semibold uppercase tracking-wide text-info-soft-foreground">
          Pesanan Tuntas Hari Ini
        </p>
        {!data.tuntas_jalur_siap ? (
          <p className="mt-2 text-xl text-warning-soft-foreground">
            Jalur serah terima ke gudang barang jadi belum aktif, jadi angka di
            sini belum bisa dihitung (bukan berarti tidak ada pesanan selesai).
          </p>
        ) : data.tuntas_hari_ini.length === 0 ? (
          <p className="mt-2 text-2xl text-info-soft-foreground/70">
            Belum ada pesanan yang diserahterimakan ke gudang hari ini.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {data.tuntas_hari_ini.map((t) => (
              <div
                key={`${t.fa_number}-${t.jam}`}
                className="flex flex-wrap items-baseline gap-x-3 text-2xl text-info-soft-foreground"
              >
                <span className="font-bold tabular-nums">{t.fa_number}</span>
                {/* Nama team OPSIONAL — banyak order perorangan nol punya,
                    dan menampilkan tanda hubung untuk sesuatu yang memang
                    nol ada cuma menambah bunyi di layar yang dibaca dari
                    jauh. */}
                {t.nama_team && <span className="font-semibold">{t.nama_team}</span>}
                {/* ⛔ K190 butir 7 (22 Sep 2026): nama pemesan dan kota TIDAK
                    ditampilkan — TV terlihat kurir, tamu, customer yang lewat.
                    Permintaan 2 Sep "nama pemesan" kalah oleh pasal yang lebih
                    baru. Backend pun tidak lagi mengirim ruasnya. */}
                <span className="ml-auto text-xl tabular-nums opacity-70">{t.jam}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ⛔ Butir 4 — SELURUH bagian tampil, termasuk yang belum. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.bagian.map((b) => {
          const k = KEADAAN[b.keadaan] || KEADAAN.NOL_TUGAS;
          return (
            <div key={b.stage_code} className={`rounded-2xl border-2 p-5 ${k.kelas}`}>
              <p className="text-3xl font-bold">{b.nama}</p>
              <p className="mt-1 text-xl font-semibold uppercase tracking-wide">
                {k.label}
              </p>
              <p className="mt-3 text-2xl tabular-nums">
                {b.tugas > 0
                  ? `${b.selesai} / ${b.tugas} tugas selesai`
                  : "—"}
              </p>
              <p className="text-xl tabular-nums opacity-80">
                beban {b.beban_atasan}
                {b.kapasitas ? ` / ${b.kapasitas}` : ""} atasan
              </p>
            </div>
          );
        })}
      </div>

      {data.belum_terjadwal?.length > 0 && (
        <div className="rounded-2xl border-2 border-warning-soft-border bg-warning-soft p-5">
          <p className="text-2xl font-semibold text-warning-soft-foreground">
            {data.belum_terjadwal.length} WO belum dijadwalkan
          </p>
          <p className="mt-1 text-xl text-warning-soft-foreground">
            Bebannya ada, tapi belum terhitung di tahap mana pun.
          </p>
        </div>
      )}
    </div>
  );
}
